import { useCallback } from 'react'
import { useSignPlatformUpload, useRecordPlatformAsset } from './backend/platform'
import { writeErrorMessage } from '../utils/writeError'

// 10 MB. A browser screenshot is well under one, and the cap is here rather
// than only in the edge function because a person who has just photographed a
// monitor with a phone should be told before the bytes travel, not after.
//
// The edge function refuses to send anything over 3.5 MB to the model, so a file
// between the two limits is stored and shown to a person but not read
// automatically. That is a deliberate gap and the dialog says so.
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

export type UploadOutcome = {
  /** Files whose bytes landed AND whose row was written. Nothing else counts. */
  done: number
  /** One line per file that did not, already written for a person to read. */
  failed: string[]
  /** Chosen but never attempted, because they are over the limit. */
  tooBig: string[]
}

/**
 * The three step upload, the same shape as useMarketingUpload and for the same
 * reasons, which are set out in full there:
 *
 *   1. Ask the Worker for a one-shot signed URL. The Worker composes the storage
 *      path, so neither the path nor the permission is ever the browser's
 *      decision.
 *   2. PUT the bytes straight to Supabase Storage with that URL.
 *   3. Tell the Worker the bytes landed, which is what writes the row.
 *
 * An upload that dies between 2 and 3 leaves an object in the bucket that no row
 * points at, which is invisible and harmless. The other ordering leaves a file in
 * the list that nobody can open, which somebody has to notice and report.
 */
export function usePlatformUpload() {
  const sign = useSignPlatformUpload()
  const record = useRecordPlatformAsset()
  const signTrigger = sign.trigger
  const recordTrigger = record.trigger

  const upload = useCallback(
    async (files: File[], requestId: string): Promise<UploadOutcome> => {
      const tooBig = files.filter((f) => f.size > MAX_UPLOAD_BYTES).map((f) => f.name)
      const sendable = files.filter((f) => f.size <= MAX_UPLOAD_BYTES)

      // Counted rather than assumed. Several screenshots can be chosen at once
      // and one can fail on its own; "2 of 3 attached" is the only honest report
      // of that, and a single "attached" toast would hide it.
      let done = 0
      const failed: string[] = []

      for (const file of sendable) {
        try {
          const signed = (await signTrigger(
            { request_id: requestId, filename: file.name },
            { skipCache: true },
          )) as { signedUrl?: string } | null
          const url = signed?.signedUrl
          if (!url) throw new Error('No upload address came back.')

          const put = await fetch(url, {
            method: 'PUT',
            headers: file.type ? { 'Content-Type': file.type } : undefined,
            body: file,
          })
          if (!put.ok) throw new Error(`Storage refused the file (${put.status}).`)

          await recordTrigger(
            {
              request_id: requestId,
              storage_path: (signed as { path?: string }).path,
              filename: file.name,
              mime_type: file.type || null,
              size_bytes: file.size,
            },
            { skipCache: true },
          )
          done += 1
        } catch (err) {
          failed.push(`${file.name}: ${writeErrorMessage(err)}`)
        }
      }

      return { done, failed, tooBig }
    },
    [signTrigger, recordTrigger],
  )

  return { upload }
}
